import { DecimalPipe } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { ProjectPeriodIntervalModel, ProjectPeriodModel, TimeModel } from "app/models/project.model";
import {
    formatProjectTime,
    getProjectPeriodIntervals,
    getProjectPeriodSegments,
    normalizeProjectPeriod,
} from "app/shared/project-periods.core";
import * as moment from "moment";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";

enum PeriodAction {
    NEW = "NEW",
    EDIT = "EDIT",
}

@Component({
    selector: "app-project-period-add-edit",
    templateUrl: "./project-period-add-edit.component.html",
    styleUrls: ["./project-period-add-edit.component.scss"],
    providers: [DecimalPipe],
    standalone: false,
})
export class ProjectPeriodAddEditComponent implements OnInit {
    public periodForm: UntypedFormGroup;
    public timeIntervals: string[];
    action: PeriodAction;
    validationMessage: string | null = null;

    private readonly period: ProjectPeriodModel | null;
    private readonly initialPeriod: ProjectPeriodModel | null;
    private readonly periods: ProjectPeriodModel[];

    constructor(
        private formBuilder: UntypedFormBuilder,
        private translationLoader: FuseTranslationLoaderService,
        public matDialogRef: MatDialogRef<ProjectPeriodAddEditComponent>,
        @Inject(MAT_DIALOG_DATA) private data: any,
        private numberPipe: DecimalPipe,
    ) {
        this.translationLoader.loadTranslations(english, spanish, catalan);

        this.period = data.period ? normalizeProjectPeriod(data.period) : null;
        this.initialPeriod = data.initialPeriod ? normalizeProjectPeriod(data.initialPeriod) : null;
        this.periods = (data.periods ?? []).map((period: ProjectPeriodModel) => normalizeProjectPeriod(period));
        this.action = this.period ? PeriodAction.EDIT : PeriodAction.NEW;
    }

    ngOnInit() {
        this.generateIntervals();
        this.createForm();
        this.updateValidation();
    }

    get intervalsFormArray(): UntypedFormArray {
        return this.periodForm.get("intervals") as UntypedFormArray;
    }

    public save() {
        const period = this.buildFormPeriod();
        if (!period) {
            this.validationMessage = "Project periods must define at least one interval";
            return;
        }

        const validationMessage = this.validatePeriod(period);
        if (validationMessage) {
            this.validationMessage = validationMessage;
            return;
        }

        this.matDialogRef.close({ period });
    }

    public cancel() {
        this.matDialogRef.close();
    }

    public addInterval(initialInterval?: ProjectPeriodIntervalModel) {
        this.intervalsFormArray.push(this.buildIntervalGroup(initialInterval));
        this.updateValidation();
    }

    public removeInterval(index: number) {
        if (this.intervalsFormArray.length <= 1) {
            return;
        }

        this.intervalsFormArray.removeAt(index);
        this.updateValidation();
    }

    public onIntervalChange() {
        this.updateValidation();
    }

    public formatInterval(interval: ProjectPeriodIntervalModel): string {
        return `${formatProjectTime(interval.startTime)} - ${formatProjectTime(interval.endTime)}`;
    }

    private createForm() {
        const sourcePeriod = this.period ?? this.initialPeriod ?? this.buildDefaultPeriod();
        const intervals = getProjectPeriodIntervals(sourcePeriod);

        this.periodForm = this.formBuilder.group({
            name: [sourcePeriod.name ?? null, [Validators.required]],
            intervals: this.formBuilder.array(
                (intervals.length ? intervals : [this.buildDefaultInterval()]).map((interval) => this.buildIntervalGroup(interval)),
            ),
        });

        this.periodForm.valueChanges.subscribe(() => this.updateValidation());
    }

    private buildIntervalGroup(interval?: ProjectPeriodIntervalModel): UntypedFormGroup {
        const sourceInterval = interval ?? this.buildDefaultInterval();

        return this.formBuilder.group({
            startTime: [this.timeString(sourceInterval.startTime), [Validators.required]],
            endTime: [this.endTimeString(sourceInterval), [Validators.required]],
        });
    }

    private generateIntervals() {
        const date = new Date(1970, 0, 1, 0, 0, 0, 0);
        const series: string[] = [];

        while (date.getDate() === 1) {
            series.push(moment(date).format("HH:mm"));
            date.setMinutes(date.getMinutes() + 30);
        }

        series.push("24:00");
        this.timeIntervals = series;
    }

    private buildFormPeriod(): ProjectPeriodModel | null {
        if (!this.periodForm.valid) {
            return null;
        }

        const name = this.periodForm.get("name").value?.trim();
        if (!name) {
            return null;
        }

        const intervals = this.intervalsFormArray.controls
            .map((control) => this.parseInterval(control as UntypedFormGroup))
            .filter((interval): interval is ProjectPeriodIntervalModel[] => !!interval)
            .flat();
        if (!intervals.length) {
            return null;
        }

        return {
            name,
            intervals: intervals.sort((left, right) => this.toMinutes(left.startTime) - this.toMinutes(right.startTime)),
        };
    }

    private parseInterval(control: UntypedFormGroup): ProjectPeriodIntervalModel[] | null {
        const startMinutes = this.parseTimeValue(control.get("startTime")?.value);
        const endMinutes = this.parseTimeValue(control.get("endTime")?.value, startMinutes, true);
        if (startMinutes === null || endMinutes === null) {
            return null;
        }

        if (startMinutes === endMinutes) {
            return [
                {
                    startTime: this.minutesToTime(0),
                    endTime: this.minutesToTime(0),
                },
            ];
        }

        if (startMinutes < endMinutes) {
            return [
                {
                    startTime: this.minutesToTime(startMinutes),
                    endTime: this.minutesToTime(endMinutes === 24 * 60 ? 0 : endMinutes),
                },
            ];
        }

        return null;
    }

    private validatePeriod(period: ProjectPeriodModel): string | null {
        const duplicate = this.periods.find((candidate) => candidate.name === period.name && candidate.name !== this.period?.name);
        if (duplicate) {
            return "Project period names must be unique";
        }

        const segments = getProjectPeriodSegments(period).sort((left, right) => left.start - right.start);
        let cursor = -1;
        for (const segment of segments) {
            if (cursor >= 0 && segment.start < cursor) {
                return "Project periods cannot overlap";
            }
            cursor = segment.end;
        }

        return null;
    }

    private updateValidation() {
        const intervalsValidation = this.validateIntervalsForm();
        if (intervalsValidation) {
            this.validationMessage = intervalsValidation;
            return;
        }

        const period = this.buildFormPeriod();
        this.validationMessage = period ? this.validatePeriod(period) : null;
    }

    private timeString(time: TimeModel) {
        return `${this.numberPipe.transform(time.hour, "2.0-0")}:${this.numberPipe.transform(time.minute, "2.0-0")}`;
    }

    private endTimeString(interval: ProjectPeriodIntervalModel): string {
        if (this.toMinutes(interval.startTime) > 0 && this.toMinutes(interval.endTime) === 0) {
            return "24:00";
        }

        return this.timeString(interval.endTime);
    }

    private parseTimeValue(value: string | null | undefined, startMinutes?: number | null, isEnd = false): number | null {
        if (!value) {
            return null;
        }

        if (value === "24:00") {
            return 24 * 60;
        }

        const [hourString, minuteString] = value.split(":");
        const hour = Number(hourString);
        const minute = Number(minuteString);

        if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
            return null;
        }

        const minutes = hour * 60 + minute;
        if (isEnd && minutes === 0 && (startMinutes ?? 0) > 0) {
            return 24 * 60;
        }

        return minutes;
    }

    private validateIntervalsForm(): string | null {
        for (const control of this.intervalsFormArray.controls) {
            const group = control as UntypedFormGroup;
            const startMinutes = this.parseTimeValue(group.get("startTime")?.value);
            const endMinutes = this.parseTimeValue(group.get("endTime")?.value, startMinutes, true);

            if (startMinutes === null || endMinutes === null) {
                continue;
            }

            if (endMinutes < startMinutes) {
                return "Project period intervals cannot end before they start";
            }
        }

        return null;
    }

    private toMinutes(time: TimeModel): number {
        return time.hour * 60 + time.minute;
    }

    private minutesToTime(totalMinutes: number): TimeModel {
        const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
        return {
            hour: Math.floor(normalized / 60),
            minute: normalized % 60,
        };
    }

    private buildDefaultPeriod(): ProjectPeriodModel {
        return {
            name: "",
            intervals: [this.buildDefaultInterval()],
        };
    }

    private buildDefaultInterval(): ProjectPeriodIntervalModel {
        return {
            startTime: { hour: 0, minute: 0 },
            endTime: { hour: 0, minute: 0 },
        };
    }
}
